/**
 * Achievement badge grid — earned badges are accent-lit; locked ones are
 * dimmed with their unlock condition shown so they read as goals, not
 * blanks. Grid shape follows the stat-card grid in `InvestmentHeader`.
 */

import { ICON_STROKE, Lock } from "@/shared/components/icons";
import { AchievementIcon } from "@/features/gamification/lib/achievementIcons";
import type { Achievement } from "@/features/gamification/lib/gamification";

export function AchievementGrid({ achievements }: { achievements: Achievement[] }) {
  const earnedCount = achievements.filter((a) => a.earned).length;

  return (
    <section className="rounded-2xl border border-border bg-surface p-5 space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold tracking-tight text-text">
          Achievements
        </h2>
        <span className="text-xs text-text-muted tabular-nums">
          {earnedCount} / {achievements.length}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {achievements.map((a) => (
          <Badge key={a.id} achievement={a} />
        ))}
      </div>
    </section>
  );
}

function Badge({ achievement }: { achievement: Achievement }) {
  const earned = achievement.earned;
  return (
    <div
      className={[
        "rounded-xl border p-3 flex flex-col items-center text-center gap-1.5 transition",
        earned
          ? "border-accent/40 bg-accent/10"
          : "border-border/50 bg-bg/30 opacity-60",
      ].join(" ")}
      title={achievement.description}
    >
      <div
        className={[
          "w-9 h-9 rounded-lg flex items-center justify-center",
          earned ? "bg-accent/20 text-accent" : "bg-text/5 text-text-muted",
        ].join(" ")}
      >
        {earned ? (
          <AchievementIcon name={achievement.icon} size={18} />
        ) : (
          <Lock size={16} strokeWidth={ICON_STROKE} />
        )}
      </div>
      <div className="text-xs font-medium text-text leading-tight">
        {achievement.title}
      </div>
      <div className="text-[10px] text-text-muted leading-snug line-clamp-2">
        {achievement.description}
      </div>
    </div>
  );
}
